/**
 * Wires `ownerSale.model.ts` to the one write, and decides whether the control
 * may be drawn at all — OND-173.
 *
 * `effectOwnerSale` on the last step, after which `onSold` hands the work's
 * record its re-read. The dialog stays mounted when shut, so closing is a state
 * change rather than an unmount — it is `close()` that returns focus to the
 * button which opened it.
 *
 * THERE IS NO BUYER CHECK TO WIRE, which is the whole of what this hook does
 * NOT have beside `useSellWizardController`. That one calls `checkBuyer` on
 * blur of the id field and holds a verdict keyed by the id it was about, so
 * Continue cannot light over a stale name. `ondo.transferee_check()` takes a
 * consignment and there is none here, so no verdict exists to hold, no probe is
 * spent, and the refusal arrives at submit — see `OwnerSaleController`'s header
 * and `OWNER_SALE_COPY.idHelp`, which says so on the field rather than letting
 * the screen imply the id was checked.
 *
 * IT READS THE WORK'S CONSIGNMENT, and that is the gate rather than a courtesy.
 * `ondo.owner_sellable_work_ids()` excludes a work under a live ACCEPTED
 * consignment — 20261014093000 decision 4, "a gallery holding an accepted
 * consignment has an interest the owner agreed to" — so the control must not
 * appear on such a work at all. `ConsignPanel` reads the same row one panel
 * above, and this asks for it again rather than having it threaded through
 * `useWorkController`: the two panels are two different acts, neither owns the
 * other's state, and making the sale's gate depend on a panel it has nothing to
 * do with would be a coupling nobody could see from either side. It is one GET
 * of a row the caller may already read.
 *
 * WHAT IT CANNOT ASK is the other half of that relation — whether an earlier
 * sale of this work is still awaiting title. Nothing the frontend can read says
 * so, and inventing an endpoint for it is not this ticket's; that refusal is
 * OND-17's 55000 at submit, with the engine's own sentence on it.
 */

import { useCallback, useEffect, useState } from "react";
import type { WorkConsignment } from "@ondo/shared";

import { effectOwnerSale, fetchWorkConsignment } from "../lib/api.ts";
import {
  OWNER_SALE_STEPS,
  describeEffect,
  describeOwnerSaleError,
  initialOwnerSaleDraft,
  nextStep,
  ownerSaleSummary,
  previousStep,
  problemWith,
  toEffectOwnerSaleRequest,
} from "./ownerSale.model.ts";
import type { OwnerSaleDraft, OwnerSaleFact, OwnerSaleStep } from "./ownerSale.model.ts";

/**
 * What the panel draws, as a discriminated union.
 *
 * `hidden` is a real state and the commonest one: a reader who is not the owner
 * of this work, a workspace that has not been given the capability, or — before
 * the read lands — a work whose consignment is not yet known. Drawing nothing is
 * right for all three, and a fourth boolean saying which would be a boolean
 * nobody renders.
 */
export type OwnerSalePanelView =
  | { kind: "hidden" }
  | { kind: "consigned" }
  | { kind: "offer"; recorded: boolean };

/** Everything the dialog renders, already decided. */
export interface OwnerSaleDialogView {
  open: boolean;
  step: OwnerSaleStep;
  /** Which steps are behind the reader, for the ticks on the stepper. */
  done: readonly OwnerSaleStep[];
  draft: OwnerSaleDraft;
  summary: OwnerSaleFact[];
  /** What the press will actually do, which depends on whether the buyer is on ONDO. */
  effect: string;
  /** Why this step may not be left, or null. The view prints it; it never computes it. */
  problem: string | null;
  submitting: boolean;
  error: string | null;
}

export interface OwnerSaleController {
  panel: OwnerSalePanelView;
  dialog: OwnerSaleDialogView;
  open: () => void;
  close: () => void;
  setField: <K extends keyof OwnerSaleDraft>(key: K, value: OwnerSaleDraft[K]) => void;
  next: () => void;
  back: () => void;
  submit: () => void;
}

/**
 * The two steps, the draft, and the one write.
 *
 * THE DRAFT SURVIVES A CLOSE, and the argument is `useSellWizardController`'s,
 * which took it from `AddHistoryDialog`: a stray Escape must not cost somebody
 * a price they negotiated for a month. It is dropped only once the registry has
 * the sale.
 *
 * AFTER A SALE IT RE-READS rather than patching. `ondo.effect_owner_sale()`
 * writes a transfer, a line of provenance and — on an off-ONDO buyer —
 * settlement rows, and it hands the rest to `saleConsequences` on the engine.
 * Nothing local could guess which of those has landed, and a screen that
 * answered "what is true" from its own optimistic copy would be the feed-as-
 * truth bug in a costlier register.
 *
 * @param workId the work being sold
 * @param subject how the summary names it — '"Marsh Light" · Agnes Thorne'
 * @param canSell `capabilities.transferWork`, from the workspace. The server's
 *   answer about whether this account holds anything sellable at all; whether
 *   THIS work may be sold is the engine's to refuse.
 * @param onSold called after the write commits, so the record can re-read
 */
export function useOwnerSaleController(
  workId: string,
  subject: string,
  canSell: boolean,
  onSold: () => void,
): OwnerSaleController {
  const [consignment, setConsignment] = useState<WorkConsignment | null | undefined>(undefined);
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<OwnerSaleStep>("terms");
  const [draft, setDraft] = useState<OwnerSaleDraft>(initialOwnerSaleDraft);
  const [submitting, setSubmitting] = useState(false);
  const [recorded, setRecorded] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;

    // Skipped entirely for a reader with no capability: the panel is hidden for
    // them whatever the answer is, and a request nobody reads is a request that
    // writes an access row for no reason.
    if (!canSell) return;

    fetchWorkConsignment(workId)
      .then((found) => {
        if (active) setConsignment(found);
      })
      // A refusal is the ordinary case for a reader who may not see the owner's
      // arrangements, and such a reader is not the owner — so `null` here lands
      // them on the offer state and the engine refuses the press with its own
      // sentence. The alternative, treating a failed read as "consigned", would
      // hide the control from the owner whenever the request merely failed.
      .catch(() => {
        if (active) setConsignment(null);
      });

    return () => {
      active = false;
    };
  }, [workId, canSell]);

  const setFieldValue = useCallback(
    <K extends keyof OwnerSaleDraft>(key: K, value: OwnerSaleDraft[K]) => {
      setDraft((held) => ({ ...held, [key]: value }));
    },
    [],
  );

  // THE ERROR IS CLEARED ON EVERY MOVE, which is the fix `useSellWizardController`
  // records rather than a tidy-up: a submit refusal rendered in the body outlived
  // the step it was raised on, so pressing Back put a sentence about the buyer's
  // id under the terms form as if it were that step's complaint about a field.
  const next = useCallback(() => {
    setError(null);
    setStep(nextStep);
  }, []);

  const back = useCallback(() => {
    setError(null);
    setStep(previousStep);
  }, []);

  const openDialog = useCallback(() => {
    setError(null);
    setRecorded(false);
    setStep("terms");
    setOpen(true);
  }, []);

  // Puts the draft DOWN rather than throwing it away. Reopening picks it back
  // up; only a recorded sale drops it.
  const close = useCallback(() => {
    setError(null);
    setStep("terms");
    setOpen(false);
  }, []);

  const submit = useCallback(() => {
    setSubmitting(true);
    setError(null);

    effectOwnerSale(workId, toEffectOwnerSaleRequest(draft))
      .then(() => {
        // The draft goes only once the registry has the sale. Dropping it before
        // the write would lose everything typed on any failure that follows.
        setDraft(initialOwnerSaleDraft());
        setStep("terms");
        setOpen(false);
        setRecorded(true);
        onSold();
      })
      .catch((cause: unknown) => {
        setError(describeOwnerSaleError(cause));
      })
      .finally(() => {
        setSubmitting(false);
      });
  }, [workId, draft, onSold]);

  return {
    panel: panelView(canSell, consignment, recorded),
    dialog: {
      open,
      step,
      done: OWNER_SALE_STEPS.slice(0, OWNER_SALE_STEPS.indexOf(step)),
      draft,
      summary: ownerSaleSummary(subject, draft),
      effect: describeEffect(draft),
      problem: problemWith(step, draft),
      submitting,
      error,
    },
    open: openDialog,
    close,
    setField: setFieldValue,
    next,
    back,
    submit,
  };
}

/**
 * Which of the three states the panel is in.
 *
 * A function rather than three conditions in the return above, so the order of
 * the tests is readable: no capability and an unanswered read are both "draw
 * nothing", an accepted consignment is the gallery's sentence, and everything
 * else is the control. An unanswered read must not fall through to the control
 * — a button that appears and then vanishes is worse than one that appears late.
 */
function panelView(
  canSell: boolean,
  consignment: WorkConsignment | null | undefined,
  recorded: boolean,
): OwnerSalePanelView {
  if (!canSell || consignment === undefined) return { kind: "hidden" };

  // ACCEPTED, not merely offered. 20261014093000 decision 4 blocks on a live
  // accepted consignment and deliberately not on an unanswered offer: "an offer
  // the gallery has not accepted binds nobody", and the relation is written the
  // same way. A screen that hid the control on an unanswered offer would be
  // stricter than the door.
  if (consignment !== null && consignment.acceptedAt !== null) return { kind: "consigned" };

  return { kind: "offer", recorded };
}
